/**
 * Creates a SyncTypeahead dropdown from a template
 * @param templateId - The ID of the template element to clone
 * @param inputId - The unique ID for the new input
 * @param name - The label/name for the dropdown
 * @param displayProperty - The property name to display in the dropdown
 * @param valueProperty - The property name to use as the value
 * @param value - The initial value for the hidden input
 * @param validSelect - Whether to require a valid selection
 * @param options - Optional array of options to override template options
 * @returns HTMLElement - The cloned and customized dropdown element
 */
export function createFromTemplate(
  templateId: string,
  inputId: string,
  name: string,
  displayProperty: string,
  valueProperty: string,
  value?: string,
  validSelect?: boolean,
  options?: { value: string; display: string }[],
): HTMLElement {
  const template = document.getElementById(templateId) as HTMLTemplateElement;

  if (!template) {
    throw new Error(`SyncTypeahead template with ID '${templateId}' not found`);
  }

  // Clone the template content
  const clone = template.content.cloneNode(true) as DocumentFragment;
  const container = clone.firstElementChild as HTMLElement;

  if (!container) {
    throw new Error(`Invalid template structure for '${templateId}'`);
  }

  // Update the container ID
  const originalContainerId = container.id;
  container.id = `${inputId}-container`;

  // Update label
  const label = container.querySelector("label");
  if (label) {
    label.setAttribute("for", inputId);
    label.textContent = "";
    label.parentElement?.removeChild(label);
  }

  // Update hidden input ID, name, and data attributes
  const hiddenInput = container.querySelector(
    'input[type="hidden"]',
  ) as HTMLInputElement;
  if (hiddenInput) {
    hiddenInput.id = inputId;
    hiddenInput.name = inputId;
    hiddenInput.setAttribute("data-display-property", displayProperty);
    hiddenInput.setAttribute("data-value-property", valueProperty);
    hiddenInput.setAttribute(
      "data-valid-sync-select",
      validSelect ? "true" : "false",
    );
    hiddenInput.setAttribute("data-sync-typeahead", "true");
    hiddenInput.setAttribute("required", "");

    if (value) {
      hiddenInput.value = value;
    }
  }

  // Update dropdown button ID
  const dropdownBtn = container.querySelector('[id$="-dropdown-btn"]');
  if (dropdownBtn) {
    dropdownBtn.id = `${inputId}-dropdown-btn`;
  }

  // Update options if provided
  if (options && options.length > 0) {
    const optionsContainer = container.querySelector(
      '[data-sync-typeahead-options="true"]',
    );
    if (optionsContainer) {
      optionsContainer.innerHTML = "";
      options.forEach((option) => {
        const optionDiv = document.createElement("div");
        optionDiv.setAttribute("data-sync-option-value", option.value);
        optionDiv.textContent = option.display;
        optionsContainer.appendChild(optionDiv);
      });
    }
  }

  return container;
}
